import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import {createPortal} from 'react-dom';
import {X} from 'lucide-react';

import './ReceptionDialog.css';

const focusableSelector = ':is(button,input,select,textarea,a[href],[tabindex]):not(:disabled):not([tabindex="-1"])';

let bodyLockCount = 0;
let bodyOverflowBeforeLock = '';

function lockBodyScroll() {
  if (bodyLockCount === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyLockCount += 1;
  return () => {
    bodyLockCount = Math.max(0, bodyLockCount - 1);
    if (bodyLockCount === 0) document.body.style.overflow = bodyOverflowBeforeLock;
  };
}

export interface ReceptionDialogProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  ariaLabel?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  showClose?: boolean;
  closeDisabled?: boolean;
  hideDefaultHeader?: boolean;
  panelClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  rootClassName?: string;
}

export function ReceptionDialog({
  open,
  title,
  onClose,
  children,
  footer,
  closeLabel = 'Close dialog',
  ariaLabel,
  initialFocusRef,
  closeOnEscape = true,
  closeOnBackdrop = true,
  showClose = true,
  closeDisabled = false,
  hideDefaultHeader = false,
  panelClassName = '',
  bodyClassName = '',
  footerClassName = '',
  rootClassName = '',
}: ReceptionDialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const hiddenHeaderLabel = hideDefaultHeader && typeof title === 'string' ? title : undefined;

  useLayoutEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      const preferred = initialFocusRef?.current
        ?? panel?.querySelector<HTMLElement>('[data-reception-dialog-autofocus]')
        ?? panel?.querySelector<HTMLElement>(`${focusableSelector}:not([data-reception-dialog-close])`)
        ?? panel;
      preferred?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      const target = restoreFocusRef.current;
      requestAnimationFrame(() => {
        if (target?.isConnected) target.focus();
      });
    };
  }, [initialFocusRef, open]);

  useEffect(() => {
    if (!open) return undefined;
    return lockBodyScroll();
  }, [open]);

  if (!open) return null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && closeOnEscape) {
      if (event.target instanceof Element && event.target.closest('[data-aa-searchable="true"]')) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) return;
    const controls = Array.from(panelRef.current.querySelectorAll<HTMLElement>(focusableSelector));
    if (!controls.length) {
      event.preventDefault();
      panelRef.current.focus();
      return;
    }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className={`aa-reception-redesign reception-dialog-root ${rootClassName}`.trim()}
      role="presentation"
      onKeyDown={handleKeyDown}
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={panelRef}
        className={`reception-dialog-panel ${panelClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel || hideDefaultHeader ? undefined : titleId}
        aria-label={ariaLabel ?? hiddenHeaderLabel}
        tabIndex={-1}
      >
        {hideDefaultHeader ? null : (
          <header className="reception-dialog-header">
            <h2 id={titleId}>{title}</h2>
          </header>
        )}
        <div className={`reception-dialog-body ${bodyClassName}`.trim()} tabIndex={-1}>{children}</div>
        {footer ? <footer className={`reception-dialog-footer ${footerClassName}`.trim()}>{footer}</footer> : null}
        {showClose ? (
          <button
            type="button"
            className="reception-dialog-close"
            data-reception-dialog-close
            aria-label={closeLabel}
            disabled={closeDisabled}
            onClick={onClose}
          >
            <X aria-hidden="true" size={19} />
          </button>
        ) : null}
      </section>
    </div>,
    document.body,
  );
}
